import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Grid,
  Paper,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Card,
  CardContent
} from '@mui/material';
import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase-config';
import { getAuth } from 'firebase/auth';

const ReviewSubmitStep = ({ data, onUpdate, onComplete, onBack }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  // Get the current user ID
  const auth = getAuth();
  const userId = auth.currentUser?.uid;

  // Get the newly purchased Twilio number
  const newTwilioNumber = data.twilioNumber;
  const carrierDetails = data.phoneData?.carrierDetails;

  // --- Dynamically construct activation code and instructions ---
  let activationCode = 'Not configured';
  let instructions = 'Instructions not available';
  let deactivationCode = carrierDetails?.deactivateCode || 'Not configured';

  if (carrierDetails && newTwilioNumber) {
    activationCode = `${carrierDetails.activationPrefix || ''}${newTwilioNumber}${carrierDetails.activationSuffix || ''}`;

    // Replace placeholder in instructions template
    if (carrierDetails.instructionsTemplate) {
      instructions = carrierDetails.instructionsTemplate.replace('{activateCode}', activationCode);
    }
  }
  // --- End dynamic construction ---

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      if (!userId) {
        throw new Error("User ID is missing. Please try logging in again.");
      }

      // Validate required data
      if (!data.phoneData?.phoneNumber || !data.phoneData?.carrier) {
        throw new Error("Phone setup information is incomplete. Please go back and complete it.");
      }

      if (!data.businessInfo?.name) {
        throw new Error("Business name is required. Please go back and complete it.");
      }

      // Create the document reference
      const businessRef = doc(db, 'businesses', userId);

      // Save all data to Firestore
      await setDoc(businessRef, {
        ...data,
        onboardingComplete: true,
        updatedAt: new Date().toISOString()
      }, { merge: true });

      // Send the auto texts
      await sendAutoTexts();

      setSuccess(true);

      // Wait a moment to show success message
      setTimeout(() => {
        onComplete();
      }, 2000);
    } catch (err) {
      console.error('Error submitting data:', err);
      setError(err.message || 'Failed to submit data');
    } finally {
      setLoading(false);
    }
  };

  const sendAutoTexts = async () => {
    try {
      if (!data.phoneData?.phoneNumber) {
        console.error("Can't send texts: phone number is missing");
        return;
      }

      // Ensure phone number is in E.164 format
      let phoneNumber = data.phoneData.phoneNumber;

      // Format the number if it's not already in E.164 format
      if (!phoneNumber.startsWith('+')) {
        // Remove any non-digit characters
        const digits = phoneNumber.replace(/\D/g, '');
        // Add country code for US numbers if needed
        if (digits.length === 10) {
          phoneNumber = `+1${digits}`;
        } else {
          phoneNumber = `+${digits}`;
        }
      }

      const businessName = data.businessInfo?.name || 'your business';

      // Only use the Cloud Function to send call forwarding instructions
      console.log('Sending call forwarding instructions via Cloud Function...');

      // Call the Cloud Function to send call forwarding instructions
      const cloudFunctionResponse = await fetch('https://us-central1-auto-text-back-web-app.cloudfunctions.net/sendCallForwardingInstructions', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          businessPhone: phoneNumber,
          twilioNumber: newTwilioNumber,
          carrier: data.phoneData?.carrier,
          businessName: businessName,
          userId: userId
        }),
      });

      const responseData = await cloudFunctionResponse.json();

      if (cloudFunctionResponse.ok) {
        if (responseData.alreadySent) {
          console.log('Call forwarding instructions were already sent previously. Skipped to avoid duplication.');
        } else {
          console.log('Call forwarding instructions sent successfully via Cloud Function');
        }
        return true;
      } else {
        throw new Error(responseData.error || 'Failed to send call forwarding instructions');
      }
    } catch (error) {
      console.error('Error sending call forwarding instructions:', error);
      // Don't throw here to allow the onboarding to complete even if texts fail
      return false;
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h6" gutterBottom>
        Review & Submit
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>
        Setup complete! You'll receive setup instructions via text shortly.
      </Alert>}

      <Typography variant="body1" sx={{ mb: 3 }}>
        Please review your information before submitting. After submission, we'll send
        text messages to your phone with instructions to set up call forwarding.
      </Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Business Information
                </Typography>
                <Typography variant="body1">
                  <strong>Name:</strong> {data.businessInfo?.name || 'Not provided'}
                </Typography>
                <Typography variant="body1">
                  <strong>Description:</strong> {data.businessInfo?.description || 'Not provided'}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Phone Setup
                </Typography>
                <Typography variant="body1">
                  <strong>Your New Business Line:</strong> {newTwilioNumber || 'Not assigned'}
                </Typography>
                <Typography variant="body1">
                  <strong>Forwarding From:</strong> {data.phoneData?.phoneNumber || 'Not provided'}
                </Typography>
                <Typography variant="body1">
                  <strong>Carrier:</strong> {carrierDetails?.name || 'Not provided'}
                </Typography>
                <Typography variant="body1" sx={{ mt: 1, fontWeight: 'bold', color: 'primary.main' }}>
                  Activation Code: {activationCode}
                </Typography>
                <Typography variant="caption" sx={{ mt: 0.5, display: 'block' }}>
                  Deactivation Code: {deactivationCode}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Call Forwarding Instructions
                </Typography>
                <Typography variant="body1">
                  {instructions}
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Introduction Message</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body1">
                  {data.introMessage || 'Not provided'}
                </Typography>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography variant="subtitle1">Common Questions & Answers ({data.commonQuestions?.length || 0})</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <div>
                  <List>
                    {data.commonQuestions?.map((qa, index) => (
                      <React.Fragment key={index}>
                        <ListItem>
                          <ListItemText
                            primary={`Q: ${qa.question}`}
                            secondary={`A: ${qa.answer}`}
                          />
                        </ListItem>
                        {index < (data.commonQuestions?.length || 0) - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </div>
              </AccordionDetails>
            </Accordion>
          </Grid>
        </Grid>
      </Paper>

      <Typography variant="body2" sx={{ mb: 2 }}>
        After submitting, you'll receive 4 text messages on {data.phoneData.phoneNumber}:
        <ol>
          <li>Welcome message introducing the service</li>
          <li>Instructions to copy the code into your phone dialer</li>
          <li>The activation code: {activationCode}</li>
          <li>Instructions on how to deactivate: {deactivationCode}</li>
        </ol>
      </Typography>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2 }}>
        <Button
          variant="outlined"
          onClick={onBack}
          disabled={loading}
        >
          Back
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
        >
          {loading ? <CircularProgress size={24} /> : 'Submit & Complete Setup'}
        </Button>
      </Box>
    </Box>
  );
};

export default ReviewSubmitStep;